import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useSocket } from "../providers/Socket";
import { usePeer } from "../providers/Peer";
import { useParams } from "react-router-dom";

const RoomPage = () => {
  const { roomId } = useParams();
  const { socket, email } = useSocket();

  const {
    peer,
    createOffer,
    createAnswer,
    setRemoteAnswer,
    sendStream,
    remoteStream,
    onIceCandidate,
    // setAudioActive,   // we'll not use these for now
    // setVideoActive,
  } = usePeer();

  const [myStream, setMyStream] = useState(null);
  const [remoteEmailId, setRemoteEmailId] = useState(null);

  // UI state for controls
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCamOn, setIsCamOn] = useState(true);
  const [isRemoteAudioOn, setIsRemoteAudioOn] = useState(true);
  const [isRemoteVideoOn, setIsRemoteVideoOn] = useState(true);

  const myVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  // Keep latest local stream in a ref to avoid stale closures
  const streamRef = useRef(null);
  useEffect(() => {
    streamRef.current = myStream;
  }, [myStream]);

  // Helper: wait until we have a local stream
  const waitForStream = useCallback(() => {
    return new Promise((resolve) => {
      if (streamRef.current) return resolve(streamRef.current);

      const interval = setInterval(() => {
        if (streamRef.current) {
          clearInterval(interval);
          resolve(streamRef.current);
        }
      }, 50);
    });
  }, []);

  // 1. Get local camera/mic
  useEffect(() => {
    const initStream = async () => {
      try {
        console.log("[ROOM] Requesting local media...");
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        console.log("[ROOM] Got local stream:", stream);

        setMyStream(stream);
        if (myVideoRef.current) {
          myVideoRef.current.srcObject = stream;
        }

        // Attach tracks to peer
        sendStream(stream);
      } catch (err) {
        console.error("[ROOM] Error accessing media devices:", err);
      }
    };

    initStream();
  }, [sendStream]);

  // 2. Join room ONLY after we have local stream
  useEffect(() => {
    if (!socket || !roomId || !myStream || !email) return;

    console.log("[ROOM] Joining room:", roomId, "as", email);
    socket.emit("join-room", { roomId, emailId: email });
  }, [socket, roomId, myStream, email]);

  // 3. Attach remote stream to video
  useEffect(() => {
    console.log("[ROOM] remoteStream changed:", remoteStream);
    if (remoteVideoRef.current && remoteStream) {
      console.log("[ROOM] Attaching remote stream to video:", remoteStream);
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current
        .play()
        .catch((e) => console.warn("[ROOM] Error playing remote video:", e));
    }
  }, [remoteStream]);

  // 4. ICE candidates
  useEffect(() => {
    onIceCandidate((candidate) => {
      console.log("[ROOM] Local ICE candidate:", candidate);
      if (remoteEmailId) {
        socket.emit("webrtc-candidate", { to: remoteEmailId, candidate });
      } else {
        console.warn("[ROOM] No remoteEmailId yet, cannot send candidate");
      }
    });
  }, [onIceCandidate, remoteEmailId, socket]);

  const handleCandidate = useCallback(
    async (payload) => {
      console.log("[ROOM] Received remote ICE payload:", payload);
      const { candidate } = payload;
      if (!candidate) {
        console.warn("[ROOM] No candidate in payload, ignoring");
        return;
      }
      try {
        await peer.addIceCandidate(candidate);
        console.log("[ROOM] ICE candidate added");
      } catch (err) {
        console.error("[ROOM] Error adding ICE candidate:", err);
      }
    },
    [peer]
  );

  // 5. Socket signaling handlers

  // Existing user: a new user joined → we are the CALLER
  const handleNewUser = useCallback(
    async ({ emailId }) => {
      console.log("[ROOM] user-joined:", emailId);
      setRemoteEmailId(emailId);

      const stream = await waitForStream();
      sendStream(stream);

      console.log("[ROOM] Creating offer for", emailId);
      const offer = await createOffer();
      console.log("[ROOM] Sending offer to", emailId);
      socket.emit("webrtc-offer", { to: emailId, offer });
    },
    [createOffer, sendStream, socket, waitForStream]
  );

  // New user: we receive an offer → we are the CALLEE
  const handleOffer = useCallback(
    async ({ from, offer }) => {
      console.log("[ROOM] Received offer from", from, offer);
      setRemoteEmailId(from);

      const stream = await waitForStream();
      sendStream(stream);

      const answer = await createAnswer(offer);
      console.log("[ROOM] Sending answer to", from);
      socket.emit("webrtc-answer", { to: from, answer });
    },
    [createAnswer, sendStream, socket, waitForStream]
  );

  // Caller receives answer from callee
  const handleAnswer = useCallback(
    async ({ from, answer }) => {
      console.log(
        "[ROOM] Received answer from",
        from,
        "state:",
        peer.signalingState
      );

      try {
        await setRemoteAnswer(answer);
        console.log("[ROOM] Remote answer set");
      } catch (err) {
        console.error("[ROOM] Error setting remote answer:", err);
      }
    },
    [setRemoteAnswer, peer]
  );

  // 6. Wire up socket events
  useEffect(() => {
    if (!socket) return;

    socket.onAny((event, ...args) => {
      console.log("[SOCKET EVENT]", event, args);
    });

    socket.on("user-joined", handleNewUser);
    socket.on("webrtc-offer", handleOffer);
    socket.on("webrtc-answer", handleAnswer);
    socket.on("webrtc-candidate", handleCandidate);

    return () => {
      socket.off("user-joined", handleNewUser);
      socket.off("webrtc-offer", handleOffer);
      socket.off("webrtc-answer", handleAnswer);
      socket.off("webrtc-candidate", handleCandidate);
      socket.offAny();
    };
  }, [socket, handleNewUser, handleOffer, handleAnswer, handleCandidate]);

  // 7. Controls: toggle my mic/cam + remote audio/video *locally*
  const toggleMyMic = () => {
    const stream = streamRef.current;
    if (!stream) return;

    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) {
      console.warn("[ROOM] No audio tracks to toggle");
      return;
    }

    const newState = !isMicOn;
    audioTracks.forEach((t) => {
      t.enabled = newState;
    });
    setIsMicOn(newState);
    console.log("[ROOM] Mic set to", newState);
  };

  const toggleMyCam = () => {
    const stream = streamRef.current;
    if (!stream) return;

    const videoTracks = stream.getVideoTracks();
    if (videoTracks.length === 0) {
      console.warn("[ROOM] No video tracks to toggle");
      return;
    }

    const newState = !isCamOn;
    videoTracks.forEach((t) => {
      t.enabled = newState;
    });
    setIsCamOn(newState);
    console.log("[ROOM] Camera set to", newState);
  };

  const toggleRemoteAudio = () => {
    if (!remoteVideoRef.current) return;

    const newState = !isRemoteAudioOn;
    remoteVideoRef.current.muted = !newState;
    setIsRemoteAudioOn(newState);
    console.log("[ROOM] Remote audio set to", newState);
  };

  const toggleRemoteVideo = () => {
    if (!remoteVideoRef.current) return;

    const newState = !isRemoteVideoOn;
    remoteVideoRef.current.style.visibility = newState ? "visible" : "hidden";
    setIsRemoteVideoOn(newState);
    console.log("[ROOM] Remote video set to", newState);
  };

  // UI
  return (
    <div style={{ padding: 20 }}>
      <h1>Room: {roomId}</h1>
      <h2>Connected to: {remoteEmailId || "No one yet"}</h2>

      {/* Controls */}
      <div style={{ marginTop: 20, marginBottom: 20 }}>
        <button onClick={toggleMyMic} style={{ marginRight: 10 }}>
          {isMicOn ? "Mute My Mic" : "Unmute My Mic"}
        </button>
        <button onClick={toggleMyCam} style={{ marginRight: 10 }}>
          {isCamOn ? "Turn Off My Camera" : "Turn On My Camera"}
        </button>
        <button onClick={toggleRemoteAudio} style={{ marginRight: 10 }}>
          {isRemoteAudioOn ? "Mute Remote Audio" : "Unmute Remote Audio"}
        </button>
        <button onClick={toggleRemoteVideo}>
          {isRemoteVideoOn ? "Hide Remote Video" : "Show Remote Video"}
        </button>
      </div>

      <div style={{ display: "flex", marginTop: 20 }}>
        <div style={{ marginRight: 20 }}>
          <h3>My Video</h3>
          <video ref={myVideoRef} autoPlay muted playsInline width={400} />
        </div>
        <div>
          <h3>Remote Video</h3>
          <video ref={remoteVideoRef} autoPlay playsInline width={400} />
        </div>
      </div>
    </div>
  );
};

export default RoomPage;
